import { z } from 'zod';
import { MVP1_SUPPORTED_INTERVAL_COUNT, MVP1_SUPPORTED_INTERVALS, PublicCheckoutStatusSchema, PublicExperienceProviderSchema, PublicExperienceStatusSchema, PublicExperienceTemplateEnum, PublicExperienceTemplateSchema, PublicExperienceTypeEnum, PublicExperienceTypeSchema, PublicExperienceFormTypeSchema, SubscriptionCancelModeEnum, SubscriptionCancelModeSchema, } from './enums';
import { AdminExperienceDetailSchema, AdminExperienceSummarySchema, ExperienceBrandingSchema, ExperienceDescriptionSchema, ExperienceTitleSchema, PayformListFilterSchema, } from './experience';
import { JotformPaymentTimingSchema } from './jotform';
import { OfferDraftSchema } from './offer';
import { billingPolicyMatchesInterval } from './billing-schedule';
import { PaymentTermsAuthoringSchema } from './payment-terms';
import { AdminSubmissionDetailSchema, AdminSubmissionSummarySchema, } from './submission';
import { LiveRecurringSummarySchema } from './billing';
import { BusinessIdSchema, CheckoutIdSchema, ExperienceIdSchema, HttpsUrlSchema, IsoTimestampSchema, PublishedRevisionSchema, RevisionSchema, StripeAccountIdSchema, SubmissionIdSchema, } from './primitives';
/**
 * Public Experiences — authenticated admin API.
 *
 * Authorization: the existing chain. A Firebase ID token is required and
 * `/api/admin/*` additionally requires `isSuperAdmin` (`validateAdmin`). There is
 * no new auth layer and no new role.
 *
 * Multi-tenant by construction: a platform admin authors experiences for ANY
 * business, so `businessId` is an explicit path parameter on EVERY route — there
 * is no implicit "current business", no session-scoped tenant and no default.
 * The server re-checks, on every single operation, that the addressed
 * experience/submission/checkout belongs to that `businessId`; a mismatch returns
 * 404 `PXE-001` (not 403) so ids cannot be enumerated.
 *
 * Responses are wrapped by the backend's existing
 * `AdminResultSchema`/`createAdminResponse`, i.e. the HTTP body is
 * `{ result: <schema below>, validationErrors: [] }`.
 */
/** Frozen route patterns (PLAN.md §6). */
export const ADMIN_PUBLIC_EXPERIENCE_ROUTES = {
    /** GET (list) and POST (create). */
    collection: '/api/admin/public-experiences/business/:businessId',
    /** GET (detail) and PUT (update draft). */
    item: '/api/admin/public-experiences/business/:businessId/:experienceId',
    publish: '/api/admin/public-experiences/business/:businessId/:experienceId/publish',
    disable: '/api/admin/public-experiences/business/:businessId/:experienceId/disable',
    eligibility: '/api/admin/public-experiences/business/:businessId/:experienceId/eligibility',
    submissions: '/api/admin/public-experiences/business/:businessId/:experienceId/submissions',
    submissionItem: '/api/admin/public-experiences/business/:businessId/:experienceId/submissions/:submissionId',
    cancelSubscription: '/api/admin/public-experiences/business/:businessId/:experienceId/submissions/:submissionId/cancel-subscription',
    reconcileCheckout: '/api/admin/public-experiences/business/:businessId/:experienceId/checkouts/:checkoutId/reconcile',
};
export const AdminBusinessParamsSchema = z
    .object({ businessId: BusinessIdSchema })
    .strict();
export const AdminExperienceParamsSchema = z
    .object({
    businessId: BusinessIdSchema,
    experienceId: ExperienceIdSchema,
})
    .strict();
export const AdminSubmissionParamsSchema = z
    .object({
    businessId: BusinessIdSchema,
    experienceId: ExperienceIdSchema,
    submissionId: SubmissionIdSchema,
})
    .strict();
export const AdminCheckoutParamsSchema = z
    .object({
    businessId: BusinessIdSchema,
    experienceId: ExperienceIdSchema,
    checkoutId: CheckoutIdSchema,
})
    .strict();
/**
 * Pagination, matching the repository convention in
 * `docs/code-patterns/infinite-scroll-endpoints.md`: `z.coerce` so query strings
 * work, page >= 1, limit 1..100 default 10.
 */
export const PaginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(10),
});
export const PaginationMetadataSchema = z
    .object({
    page: z.number().int().min(1),
    limit: z.number().int().min(1).max(100),
    total: z.number().int().min(0),
    totalPages: z.number().int().min(0),
})
    .strict();
/** `{ data, pagination }` — the repository's paginated response envelope. */
export const createPaginatedResponseSchema = (itemSchema) => z
    .object({
    data: z.array(itemSchema),
    pagination: PaginationMetadataSchema,
})
    .strict();
/**
 * The editable payload. `type`/`template` are literals, not free enums: MVP1
 * accepts `FORM` + `CONTACT_FORM_V1` only, and sending anything else fails to
 * parse rather than being coerced.
 */
const AdminExperienceWritableSchema = z
    .object({
    formType: PublicExperienceFormTypeSchema.optional(),
    internalName: ExperienceTitleSchema.optional(),
    paymentTerms: PaymentTermsAuthoringSchema.optional(),
    jotformPaymentTiming: JotformPaymentTimingSchema.optional(),
    type: PublicExperienceTypeSchema,
    template: PublicExperienceTemplateSchema,
    title: ExperienceTitleSchema,
    description: ExperienceDescriptionSchema,
    branding: ExperienceBrandingSchema,
    offer: OfferDraftSchema,
})
    .strict();
/**
 * Runtime refinement restricting MVP1 input: `FORM` + `CONTACT_FORM_V1` +
 * Stripe-supported recurrence. The persisted and transport schemas stay wider so
 * widening the supported set later is not a schema break — only this refinement
 * and `MVP1_SUPPORTED_INTERVALS` change.
 */
const refineMvp1Experience = (payload, ctx) => {
    if (!billingPolicyMatchesInterval(payload.offer.billingPolicy, payload.offer.interval, payload.offer.intervalCount)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['offer', 'billingPolicy'],
            message: 'Schedule frequency must match the offer',
        });
    }
    if (payload.type !== PublicExperienceTypeEnum.FORM) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['type'],
            message: 'MVP1 supports the FORM experience type only',
        });
    }
    if (payload.template !== PublicExperienceTemplateEnum.CONTACT_FORM_V1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['template'],
            message: 'MVP1 supports the CONTACT_FORM_V1 template only',
        });
    }
    if (!MVP1_SUPPORTED_INTERVALS.includes(payload.offer.interval)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['offer', 'interval'],
            message: `MVP1 supports these billing intervals only: ${MVP1_SUPPORTED_INTERVALS.join(', ')}`,
        });
    }
    if (payload.offer.intervalCount !== MVP1_SUPPORTED_INTERVAL_COUNT) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['offer', 'intervalCount'],
            message: `MVP1 supports intervalCount ${MVP1_SUPPORTED_INTERVAL_COUNT} only`,
        });
    }
};
/**
 * `POST /api/admin/public-experiences/business/:businessId`
 * Creates a DRAFT. Nothing is published and no provider object is created yet.
 */
export const CreateAdminExperienceRequestSchema = AdminExperienceWritableSchema.superRefine(refineMvp1Experience);
/**
 * `PUT /api/admin/public-experiences/business/:businessId/:experienceId`
 * Saves the draft only; live behaviour is unchanged until `publish`.
 * `expectedDraftRevision` is mandatory — a mismatch is 409 `PXE-010`.
 */
export const UpdateAdminExperienceRequestSchema = AdminExperienceWritableSchema.extend({
    expectedDraftRevision: RevisionSchema,
}).superRefine(refineMvp1Experience);
/**
 * `POST .../publish` — freezes the current draft as the live snapshot, creates or
 * reuses the provider Product, creates a new Price when amount or recurrence
 * changed (archiving the previous one) and writes the public projection.
 * Idempotent per `(experienceId, revision)`.
 */
export const PublishAdminExperienceRequestSchema = z
    .object({ expectedDraftRevision: RevisionSchema })
    .strict();
/**
 * `POST .../disable` — blocks new checkouts. Sessions already issued may finish
 * (<= 60 min) and existing subscriptions are untouched (PLAN D11).
 */
export const DisableAdminExperienceRequestSchema = z
    .object({ expectedDraftRevision: RevisionSchema })
    .strict();
/** List/detail responses. */
export const ListAdminExperiencesQuerySchema = PaginationQuerySchema.extend({
    displayStatus: PayformListFilterSchema.optional(),
    search: z.string().trim().max(120).optional(),
    status: PublicExperienceStatusSchema.optional(),
    formType: PublicExperienceFormTypeSchema.optional(),
});
export const ListAdminExperiencesResponseSchema = createPaginatedResponseSchema(AdminExperienceSummarySchema).extend({
    /** Unconverted historical/mixed records, shown separately from either typed tab. */
    unclassified: z.array(AdminExperienceSummarySchema).optional(),
});
export const AdminExperienceResponseSchema = AdminExperienceDetailSchema;
/**
 * `GET .../eligibility` — can this business actually take a recurring payment?
 * Read-only; it never mutates configuration. Re-checked again on every checkout
 * create, so a green answer here is informational, not a reservation.
 */
export const AdminExperienceEligibilityResponseSchema = z
    .object({
    eligible: z.boolean(),
    provider: PublicExperienceProviderSchema.nullable(),
    providerAccountId: StripeAccountIdSchema.nullable(),
    /** From `financialDetails.onlineProcessingConfig.rate` (online, never POS). */
    onlineProcessingRate: z.number().min(0).max(1).nullable(),
    /** The fee percent that would be stamped on the next publish. */
    applicationFeePercent: z.number().min(0).max(100).nullable(),
    chargesEnabled: z.boolean(),
    canProcessPayments: z.boolean(),
    /** Machine-readable blockers, e.g. `PXE-006`, `PXE-005`. Empty when eligible. */
    blockers: z
        .array(z
        .object({
        code: z.string().min(1).max(32),
        message: z.string().min(1).max(500),
    })
        .strict())
        .default([]),
    checkedAt: IsoTimestampSchema,
})
    .strict();
/** `GET .../submissions?page&limit` — no live provider read per row. */
export const ListAdminSubmissionsQuerySchema = PaginationQuerySchema;
export const ListAdminSubmissionsResponseSchema = createPaginatedResponseSchema(AdminSubmissionSummarySchema);
/** `GET .../submissions/:submissionId` — one live billing read. */
export const AdminSubmissionResponseSchema = AdminSubmissionDetailSchema;
/**
 * `POST .../submissions/:submissionId/cancel-subscription`
 *
 * The only write this feature performs against an existing subscription, and it
 * is admin-only: there is no customer-facing cancel in MVP1. `AT_PERIOD_END` is
 * the default; `IMMEDIATE` stops billing at once and never issues a refund.
 * `confirmCancellation` must be `true` so a stray click cannot cancel billing.
 */
export const CancelSubscriptionRequestSchema = z
    .object({
    mode: SubscriptionCancelModeSchema.default(SubscriptionCancelModeEnum.AT_PERIOD_END),
    confirmCancellation: z.literal(true),
    /** Optional operator note recorded for the audit trail. */
    reason: z.string().trim().max(500).optional(),
})
    .strict();
/**
 * Cancellation result. Returns the refreshed live summary so the UI never has to
 * guess what changed, and states plainly that no refund was issued.
 */
export const CancelSubscriptionResponseSchema = z
    .object({
    submissionId: SubmissionIdSchema,
    mode: SubscriptionCancelModeSchema,
    cancelAtPeriodEnd: z.boolean(),
    canceledAt: IsoTimestampSchema.nullable(),
    effectiveAt: IsoTimestampSchema.nullable(),
    refundIssued: z.literal(false),
    billing: LiveRecurringSummarySchema,
})
    .strict();
/**
 * `POST .../checkouts/:checkoutId/reconcile`
 *
 * Operator recovery for an uncertain attempt: re-read the provider session by the
 * stored id (or replay the idempotent create) and apply the forward-only
 * transition that the evidence supports. It never creates a second subscription
 * and never moves a terminal checkout.
 */
export const ReconcileCheckoutRequestSchema = z
    .object({
    /** Operator note for the audit trail. */
    reason: z.string().trim().min(1).max(500),
})
    .strict();
export const ReconcileCheckoutResponseSchema = z
    .object({
    checkoutId: CheckoutIdSchema,
    statusBefore: PublicCheckoutStatusSchema,
    statusAfter: PublicCheckoutStatusSchema,
    /** False when the provider evidence justified no transition. */
    changed: z.boolean(),
    /** What the provider reported, normalized. Never a raw provider object. */
    findings: z
        .array(z
        .object({
        code: z.string().min(1).max(64),
        message: z.string().min(1).max(500),
    })
        .strict())
        .default([]),
    reconciledAt: IsoTimestampSchema,
})
    .strict();
/** Admin "copy link" / "preview" helper output. */
export const AdminExperienceLinkSchema = z
    .object({
    publicUrl: HttpsUrlSchema,
    publishedRevision: PublishedRevisionSchema.nullable(),
})
    .strict();
